import React, { useState } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const TextInput = ({
  label,
  value,
  onChange,
  placeholder = "",
  className = "",
  error = "",
  required = false,
  disabled = false,
  type = "text", // text, email, password, number, etc.
  variant = "input", // input or textarea
  rows = 4,
  icon: Icon = null,
  maxLength = null,
  showCharCount = false,
  id,
  name,
  helperText = "",
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false);

  // Determine actual input type (handle password visibility toggle)
  const inputType = type === 'password' && showPassword ? 'text' : type;
  const isPasswordField = type === 'password';
  // Base classes for the input/textarea
  const baseClasses = `
    w-full border-0 bg-gray-50 rounded-lg text-sm focus:bg-white focus:ring-2 focus:ring-indigo-500 transition-all
    disabled:opacity-50 disabled:cursor-not-allowed
    ${error ? 'ring-2 ring-red-500 bg-red-50' : ''}
  `.trim().replace(/\s+/g, ' ');

  // Padding classes based on whether there's an icon or password toggle
  const paddingClasses = Icon
    ? "pl-8 pr-3 py-2"
    : variant === "textarea"
      ? "px-3 py-2"
      : isPasswordField
        ? "pl-3 pr-10 py-2" // Extra padding for eye icon
        : "px-3 py-2";

  const inputClasses = `${baseClasses} ${paddingClasses} ${className}`;

  // Character count color logic
  const getCharCountColor = (length, max) => {
    if (!max) return 'bg-gray-100 text-gray-500';
    const percentage = (length / max) * 100;
    if (percentage > 90) return 'bg-red-100 text-red-600';
    if (percentage > 70) return 'bg-yellow-100 text-yellow-600';
    return 'bg-gray-100 text-gray-500';
  };

  return (
    <div>
      {/* Label */}
      {label && (
        <label htmlFor={id || name} className="block w-full text-sm font-semibold text-gray-800 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      {/* Input Container */}
      <div className="relative">
        {/* Icon */}
        {Icon && (
          <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
            <Icon className="h-3 w-3 text-gray-400" />
          </div>
        )}

        {/* Input or Textarea */}
        {variant === "textarea" ? (
          <textarea
            id={id || name}
            name={name}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            disabled={disabled}
            rows={rows}
            maxLength={maxLength}
            className={`${inputClasses} resize-none`}
            required={required}
            {...props}
          />
        ) : (
          <input
            type={inputType}
            id={id || name}
            name={name}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            disabled={disabled}
            maxLength={maxLength}
            className={inputClasses}
            required={required}
            {...props}
          />
        )}

        {/* Password Visibility Toggle */}
        {isPasswordField && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
            tabIndex={-1}
          >
            {showPassword ? (
              <EyeSlashIcon className="h-5 w-5" aria-hidden="true" />
            ) : (
              <EyeIcon className="h-5 w-5" aria-hidden="true" />
            )}
          </button>
        )}

        {/* Character Count */}
        {showCharCount && maxLength && variant === "textarea" && (
          <div className="absolute bottom-1 right-2">
            <span className={`text-xs px-1 py-0.5 rounded ${getCharCountColor(value?.length || 0, maxLength)}`}>
              {value?.length || 0}/{maxLength}
            </span>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <p className="mt-1 text-xs text-red-600">{error}</p>
      )}

      {/* Helper Text */}
      {!error && helperText && (
        <p className="mt-1 text-xs text-gray-500">{helperText}</p>
      )}
    </div>
  );
};

export default TextInput;